// Sound effects, synthesised live with Web Audio in the same tone colours as the band (D-02; no audio files).
import { hz } from './score';

export const SFX = ['step', 'stepWood', 'doorIn', 'doorOut', 'lift', 'place', 'blocked', 'turn', 'stash', 'swish', 'tick',
  'shake', 'fruitDrop', 'splash', 'nibble', 'bite', 'catch', 'dig', 'coin', 'mailOpen', 'chatPop', 'emote', 'pickUp'] as const;
export type SfxName = (typeof SFX)[number];

/** One oscillator from `t` to `end`, into `out`. */
export function osc(ctx: BaseAudioContext, type: OscillatorType, f: number, t: number, end: number, out: AudioNode): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  o.connect(out);
  o.start(t);
  o.stop(end);
  return o;
}

/** A band-passed burst of the shared noise buffer: shakers, rims, rustles, footsteps. */
export function noiseBurst(ctx: BaseAudioContext, noise: AudioBuffer, t: number, freq: number, q: number, peak: number, decay: number, out: AudioNode): void {
  const src = ctx.createBufferSource();
  src.buffer = noise;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = freq;
  bp.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(peak, t + 0.004);
  g.gain.setTargetAtTime(0, t + 0.004, decay / 3);
  src.connect(bp).connect(g).connect(out);
  src.start(t, Math.random() * 0.8);
  src.stop(t + decay * 2 + 0.05);
}

export function playSfx(ctx: BaseAudioContext, out: AudioNode, noise: AudioBuffer, name: SfxName): void {
  const t = ctx.currentTime + 0.005;
  const noiseHit = (at: number, freq: number, q: number, peak: number, decay: number) => noiseBurst(ctx, noise, at, freq, q, peak, decay, out);
  const tone = (type: OscillatorType, f0: number, f1: number, peak: number, dur: number, at = t) => {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(peak, at + 0.005);
    g.gain.setTargetAtTime(0, at + 0.005, dur / 3);
    g.connect(out);
    const o = osc(ctx, type, f0, at, at + dur * 2 + 0.05, g);
    o.frequency.setValueAtTime(f0, at);
    if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(f1, at + dur);
  };
  const bell = (m: number, at: number, peak = 0.12) => {
    for (const [ratio, amp, decay] of [[1, 1, 0.9], [2.76, 0.3, 0.3], [5.4, 0.1, 0.12]] as const) {
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(peak * amp, at + 0.004);
      g.gain.setTargetAtTime(0, at + 0.004, decay);
      g.connect(out);
      osc(ctx, 'sine', hz(m) * ratio, at, at + decay * 6, g);
    }
  };
  const swish = () => {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.4;
    bp.frequency.setValueAtTime(700, t);
    bp.frequency.exponentialRampToValueAtTime(4200, t + 0.25);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.09, t + 0.08);
    g.gain.linearRampToValueAtTime(0, t + 0.3);
    src.connect(bp).connect(g).connect(out);
    src.start(t);
    src.stop(t + 0.35);
    bell(91, t + 0.18, 0.05);
  };
  switch (name) {
    case 'step': noiseHit(t, 700 + Math.random() * 500, 1.1, 0.05, 0.05); break;
    case 'stepWood': noiseHit(t, 1300 + Math.random() * 400, 2, 0.045, 0.04); tone('sine', 190, 150, 0.04, 0.05); break;
    case 'doorIn': bell(79, t); bell(84, t + 0.13); break;
    case 'doorOut': bell(84, t); bell(79, t + 0.13); break;
    case 'lift': tone('sine', 420, 760, 0.09, 0.08); break;
    case 'place': noiseHit(t, 650, 2.5, 0.16, 0.08); tone('sine', 170, 115, 0.16, 0.1); break;
    case 'blocked': tone('triangle', 220, 196, 0.07, 0.09); tone('triangle', 165, 147, 0.07, 0.12, t + 0.1); break;
    case 'turn': tone('sine', 620, 880, 0.06, 0.06); break;
    case 'stash': tone('sine', 760, 380, 0.08, 0.12); break;
    case 'swish': swish(); break;
    case 'tick': tone('sine', 1250, 1250, 0.035, 0.02); break;
    case 'shake': noiseHit(t, 1800, 0.8, 0.07, 0.25); noiseHit(t + 0.12, 2200, 0.8, 0.06, 0.2); break;          // leaves rustle
    case 'fruitDrop': tone('sine', 300, 140, 0.12, 0.12); noiseHit(t + 0.05, 500, 2, 0.08, 0.06); break;          // soft thud
    case 'splash': noiseHit(t, 1200, 0.7, 0.14, 0.35); tone('sine', 520, 260, 0.05, 0.2); break;
    case 'nibble': tone('sine', 880, 760, 0.04, 0.04); break;
    case 'bite': tone('triangle', 330, 220, 0.12, 0.08); noiseHit(t, 900, 1.5, 0.1, 0.12); break;
    case 'catch': bell(76, t); bell(79, t + 0.1); bell(84, t + 0.2); break;                                    // rising arpeggio
    case 'dig': noiseHit(t, 420, 1.2, 0.16, 0.12); noiseHit(t + 0.16, 380, 1.2, 0.12, 0.1); break;
    case 'coin': bell(88, t, 0.1); bell(93, t + 0.07, 0.1); break;
    case 'mailOpen': swish(); bell(84, t + 0.15, 0.06); break;
    case 'chatPop': tone('sine', 660, 990, 0.05, 0.05); break;
    case 'emote': bell(81, t, 0.07); break;
    case 'pickUp': tone('sine', 520, 880, 0.08, 0.07); break;
  }
}
